import { useTranslation } from 'react-i18next';

/**
 * One string for every place the customer meets cold-stored delivery files
 * (#2678): the download action in the Requests table, the download control on
 * the request itself, and the placeholder where the archived file would have
 * played.
 *
 * All three say the same thing. Under File Retention there is no unarchive
 * step to offer (not self-service, and no longer a support promise either), so
 * the copy only states that the files are gone. be-core's download refusal
 * (RequestFilesDownloadState::refusalMessage) says the same.
 *
 * Lives in the `actions` namespace because that is the one the table's action
 * copy already uses, and it is loaded globally (LocalizationClient.types).
 */
const requestFilesArchivedKey = 'download_files_archived';

/**
 * One short sentence: the tightest place it renders is a tooltip in the
 * Requests table's Action column, where longer copy wrapped over the row above.
 */
const requestFilesArchivedDefault = 'These files are no longer available.';

const useRequestFilesArchivedMessage = (): string => {
  const { t } = useTranslation('actions');

  return t(requestFilesArchivedKey, { defaultValue: requestFilesArchivedDefault });
};

/**
 * The other half of the same story, for the window where a restore is already
 * running: still not downloadable, but contacting support again would not move
 * it along, so it says so instead.
 */
const requestFilesRestoringKey = 'download_files_restoring';

const requestFilesRestoringDefault = 'Unarchiving — this can take up to 24 hours.';

const useRequestFilesRestoringMessage = (): string => {
  const { t } = useTranslation('actions');

  return t(requestFilesRestoringKey, { defaultValue: requestFilesRestoringDefault });
};

export {
  useRequestFilesArchivedMessage,
  useRequestFilesRestoringMessage,
  requestFilesArchivedKey,
  requestFilesArchivedDefault,
  requestFilesRestoringKey,
  requestFilesRestoringDefault,
};
